import React from 'react'
import PatternBg from '../components/PatternBg'
import ListingCard from '../components/ListingCard'
import AppBanner from '../components/AppBanner'
import MapView from '../components/MapView'
import CategoryTabs from '../components/CategoryTabs'
import FiltersBar from '../components/FiltersBar'
import { useTranslation } from 'react-i18next'
import { searchRooms, imageUrl } from '../api/rooms'

/* -------------------------------------------------------
   CONSTANTS
------------------------------------------------------- */
const PAGE_SIZE = 8

const BACKEND_TYPE = {
  hotel: 'Hotel',
  appartement: 'Appartement',
  maison_meublee: 'Furnished',
  studio: 'Studio',
  villa: 'Villa',
  ForRent: 'ForRent',
  Studio: 'Studio',
  TwoRoom: 'TwoRoom',
  House: 'House',
  SharedRoom: 'SharedRoom',
  Parking: 'Parking',
}

/* -------------------------------------------------------
   HELPERS
------------------------------------------------------- */
function toNum(v, fallback = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function isoOrUndef(s) {
  if (!s) return undefined
  const d =
    typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
      ? new Date(s + 'T00:00:00')
      : new Date(s)
  return isNaN(d.getTime()) ? undefined : d.toISOString()
}

function mapRoomToUi(r) {
  const imgsRaw = Array.isArray(r.imageUrl) ? r.imageUrl : r.images ?? []
  const imgs = imgsRaw.filter(Boolean).map(imageUrl)

  return {
    id: r.id ?? r.roomID,
    title: r.title ?? r.name ?? '',
    imageUrl: imgs,
    image:
      imgs[0] ||
      r.image ||
      'https://via.placeholder.com/800x450?text=Locapiol',
    city: r.city ?? r.location?.city ?? '',
    district: r.district ?? r.location?.district ?? '',
    verified: !!(r.verified ?? r.isVerified),
    type: r.type ?? r.roomType ?? null,
    tags: r.tags ?? (r.type ? [r.type] : []),
    price: {
      amount:
        typeof r.price === 'number'
          ? r.price
          : toNum(r.price?.amount, 0),
      currency: r.price?.currency ?? r.currency ?? 'XAF',
      unit: r.price?.unit ?? r.unit ?? '',
    },
  }
}

/* -------------------------------------------------------
   PAGE
------------------------------------------------------- */
export default function Annonces() {
  const { t } = useTranslation()

  /* ---------------- STATE ---------------- */
  const [category, setCategory] = React.useState(null)
  const [filters, setFilters] = React.useState({
    city: '',
    minPrice: '',
    maxPrice: '',
    furnished: false,
    category: null,
    checkin: '',
    checkout: '',
    adults: 1,
    children: 0,
    infants: 0,
    currency: 'XAF',
  })

  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  const [page, setPage] = React.useState(1)
  const [totalCount, setTotalCount] = React.useState(0)

  /* ---------------- DATA LOAD ---------------- */
  React.useEffect(() => {
    const controller = new AbortController()
    const debounce = setTimeout(load, 250)

    async function load() {
      setLoading(true)
      setError(null)

      try {
        const q = {
          city: filters.city || undefined,
          from: isoOrUndef(filters.checkin),
          to: isoOrUndef(filters.checkout),
          minPrice:
            filters.minPrice !== '' ? toNum(filters.minPrice) : undefined,
          maxPrice:
            filters.maxPrice !== '' ? toNum(filters.maxPrice) : undefined,
          furnished: filters.furnished || undefined,
          currency: filters.currency || undefined,
          type: BACKEND_TYPE[category || filters.category] || undefined,
          adults: toNum(filters.adults, 1),
          children: toNum(filters.children, 0),
          infants: toNum(filters.infants, 0),
          pageNumber: page,
          pageSize: PAGE_SIZE,
        }

        const res = await searchRooms(q, {
          signal: controller.signal,
        })

        const raw = Array.isArray(res)
          ? res
          : res?.items ?? res?.Items ?? []

        setItems(raw.map(mapRoomToUi))
        setTotalCount(
          res?.totalCount ??
            res?.TotalCount ??
            raw.length
        )
      } catch (e) {
        if (e.name === 'AbortError') return
        console.error(e)
        setError(
          t('errors.loadFailed', 'Une erreur est survenue')
        )
      } finally {
        setLoading(false)
      }
    }

    return () => {
      clearTimeout(debounce)
      controller.abort()
    }
  }, [category, filters, page, t])

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  /* -------------------------------------------------------
     RENDER
  ------------------------------------------------------- */
  return (
    <>
      {/* ---------------- HERO ---------------- */}
      <PatternBg>
        <section className="section">
          <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1
                className="text-4xl font-extrabold leading-tight"
                dangerouslySetInnerHTML={{
                  __html: t('hero.title'),
                }}
              />
              <p className="text-muted mt-2">
                {t('hero.lead')}
              </p>
            </div>
            <MapView items={items} />
          </div>
        </section>
      </PatternBg>

      {/* ---------------- LISTINGS ---------------- */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FiltersBar value={filters} onChange={setFilters} />

          <div className="my-6">
            <CategoryTabs
              active={category}
              onChange={(c) => {
                setCategory(c)
                setPage(1)
              }}
            />
          </div>

          <h2 className="section-title mb-4">
            {t('listings.title')}
          </h2>

          {error && (
            <div className="mb-4 text-red-600 bg-red-50 border rounded-xl px-3 py-2">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-16 text-gray-500">
              {t('listings.loading', 'Chargement…')}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              {t(
                'listings.filter',
                'Aucune annonce ne correspond à vos critères'
              )}
            </div>
          ) : (
            <>
              {/* GRID AIRBNB */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {items.map((item) => (
                  <ListingCard
                    key={item.id}
                    item={item}
                  />
                ))}
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-10">
                  <button
                    disabled={page === 1}
                    onClick={() =>
                      setPage((p) => Math.max(1, p - 1))
                    }
                    className="px-3 py-1 rounded border disabled:opacity-30"
                  >
                    ‹
                  </button>

                  {Array.from({ length: totalPages }).map(
                    (_, i) => (
                      <button
                        key={i}
                        onClick={() => setPage(i + 1)}
                        className={`px-3 py-1 rounded border ${
                          page === i + 1
                            ? 'bg-black text-white'
                            : ''
                        }`}
                      >
                        {i + 1}
                      </button>
                    )
                  )}

                  <button
                    disabled={page === totalPages}
                    onClick={() =>
                      setPage((p) =>
                        Math.min(totalPages, p + 1)
                      )
                    }
                    className="px-3 py-1 rounded border disabled:opacity-30"
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <AppBanner />
    </>
  )
}
